import React, { useState } from 'react';
import { Check, ThumbsUp, Minus, ThumbsDown, Send } from 'lucide-react';
import QuoteAcceptanceForm from '../QuoteAcceptanceForm';
import { quotesApi } from '../../services/api';
import toast from 'react-hot-toast';
import { formatEUR } from './lib';

// ── Alternative-Auswahl (altes + neues System) ───────────────────────────────
export const AlternativePicker = ({ service, theme, selectedAlternatives, onSelect }) => {
  const btn = (active) => ({
    width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: theme.radius,
    fontSize: 13, transition: 'all .15s', cursor: 'pointer', marginBottom: 6,
    background: active ? theme.primary : theme.surface,
    color: active ? '#fff' : theme.textPrimary,
    border: `1px solid ${active ? theme.primary : theme.surfaceBorder}`,
  });

  if (service.alternatives && service.alternatives.length > 0) {
    return (
      <div style={{ marginTop: 10 }}>
        <p style={{ fontSize: 12, fontWeight: 500, color: theme.textSecondary, marginBottom: 6 }}>Wählen Sie eine Option:</p>
        {service.alternatives.map(alt => {
          const active = (selectedAlternatives[service.id] || service.selectedAlternativeId) === alt.id;
          return (
            <button key={alt.id} style={btn(active)} onClick={() => onSelect(service.id, alt.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 500 }}>{alt.title}{active ? ' ✓' : ''}</span>
                <span style={{ fontWeight: 600 }}>{formatEUR(alt.price)}</span>
              </div>
              {alt.description && <p style={{ fontSize: 12, marginTop: 2, opacity: .85 }}>{alt.description}</p>}
            </button>
          );
        })}
      </div>
    );
  }

  if (service.hasAlternative && service.alternativeName) {
    const sel = selectedAlternatives[service.id];
    const mainActive = !sel || sel === 'main';
    return (
      <div style={{ marginTop: 10 }}>
        <p style={{ fontSize: 12, fontWeight: 500, color: theme.textSecondary, marginBottom: 6 }}>Alternative wählen:</p>
        <button style={btn(mainActive)} onClick={() => onSelect(service.id, 'main')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontWeight: 500 }}>{service.name}{mainActive ? ' ✓' : ''}</span>
            <span style={{ fontWeight: 600 }}>{formatEUR(service.price)}</span>
          </div>
        </button>
        <button style={btn(sel === 'alternative')} onClick={() => onSelect(service.id, 'alternative')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontWeight: 500 }}>{service.alternativeName}{sel === 'alternative' ? ' ✓' : ''}</span>
            <span style={{ fontWeight: 600 }}>{formatEUR(service.alternativePrice)}</span>
          </div>
        </button>
      </div>
    );
  }
  return null;
};


// ── Angebots-Kennzeichnung: Nummer, Datum, Gültigkeit ────────────────────────
// Rechtlich formfrei, aber Standard fuer Referenzierbarkeit + Beweisbarkeit:
// die Annahme bezieht sich auf genau diese Angebotsnummer.
// Standard-Gueltigkeit fuer alle Angebote (8 Tage ab Erstellung)
// Intro-/Freitexte kommen aus dem Editor entweder als HTML (<p>, <strong>, <ul>)
// oder als reiner Text mit Zeilenumbruechen. Vorher wurden sie IMMER als Text
// gerendert — HTML-Angebote zeigten dem Kunden woertlich "<p>Hallo Herr ...".
const looksLikeHtml = (s) =>
  typeof s === 'string' && /<\/?(p|br|ul|ol|li|strong|em|b|i|h[1-6]|div|span|a)\b[^>]*>/i.test(s);

export const RichText = ({ value, style, className }) => {
  if (!value) return null;
  if (looksLikeHtml(value)) {
    return (
      <div
        className={['rt-html', className].filter(Boolean).join(' ')}
        style={style}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }
  // Klartext: Markdown-Fettung wird oft mitgeliefert ("**wichtig**") und stand
  // sonst mit Sternchen im Angebot beim Kunden. Erst escapen, dann nur diese
  // eine Auszeichnung zulassen — kein fremdes HTML kommt durch.
  const esc = value
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const withBold = esc
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s.,;:!?)]|$)/g, '$1<em>$2</em>');
  if (withBold === esc) {
    return (
      <div className={className} style={{ whiteSpace: 'pre-wrap', ...(style || {}) }}>
        {value}
      </div>
    );
  }
  return (
    <div
      className={className}
      style={{ whiteSpace: 'pre-wrap', ...(style || {}) }}
      dangerouslySetInnerHTML={{ __html: withBold }}
    />
  );
};

export const QUOTE_VALID_DAYS = 8;

export const quoteValidUntil = (quote) => {
  if (quote?.validUntil) return new Date(quote.validUntil);
  const d = new Date(quote?.createdAt || Date.now());
  d.setDate(d.getDate() + QUOTE_VALID_DAYS);
  return d;
};

export const QuoteMetaBlock = ({ quote, theme, company }) => {
  if (!quote?.quoteNumber) return null;
  const fmt = (d) => new Date(d).toLocaleDateString('de-DE');
  const cust = quote.customerData || {};
  // Anschrift wie im Geschaeftsbrief: jede Angabe auf eigener Zeile.
  // 'city' enthaelt teils "PLZ Ort, Land" — das wird ebenfalls aufgetrennt.
  const addressLines = (street, city) => [
    street,
    ...String(city || '').split(',').map((t) => t.trim()),
  ].filter(Boolean);
  const custLines = addressLines(cust.address, cust.city);
  const compLines = addressLines(company?.address, company?.city);
  const label = { fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: theme.textMuted, marginBottom: 4 };
  return (
    <>
    {(cust.company || cust.name || company?.name) && (
      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap',
        margin: '0 0 16px', fontSize: 14, lineHeight: 1.6, color: theme.textSecondary,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}>
        {(cust.company || cust.name) && (
          <div style={{ minWidth: 220 }}>
            <div style={label}>Angebot für</div>
            {cust.company && <div style={{ color: theme.textPrimary, fontWeight: 600 }}>{cust.company}</div>}
            {cust.name && <div style={{ color: theme.textPrimary }}>{cust.name}</div>}
            {custLines.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        )}
        {company?.name && (
          <div style={{ minWidth: 220, textAlign: 'right' }}>
            <div style={label}>Angebot von</div>
            <div style={{ color: theme.textPrimary, fontWeight: 600 }}>{company.name}</div>
            {company.contactPerson && <div style={{ color: theme.textPrimary }}>{company.contactPerson}</div>}
            {compLines.map((l, i) => <div key={i}>{l}</div>)}
            {company.phone && <div style={{ marginTop: 6 }}>{company.phone}</div>}
            {company.email && <div>{company.email}</div>}
          </div>
        )}
      </div>
    )}
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      margin: '0 0 18px', fontSize: 12.5, color: theme.textMuted,
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      <span style={{
        padding: '3px 10px', borderRadius: 999, fontWeight: 700, letterSpacing: 1.5,
        textTransform: 'uppercase', fontSize: 10.5,
        background: theme.dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
        color: theme.textSecondary, border: `1px solid ${theme.surfaceBorder}`,
      }}>Angebot</span>
      <span>Nr. <strong style={{ color: theme.textSecondary }}>{quote.quoteNumber}</strong></span>
      {quote.createdAt && <span>· vom {fmt(quote.createdAt)}</span>}
      <span>· gültig bis <strong style={{ color: theme.textSecondary }}>{fmt(quoteValidUntil(quote))}</strong></span>
    </div>
    </>
  );
};

// ── Zahlungsbedingungen / Konditionen / AGB ──────────────────────────────────
// Interne Zahlungs-Schlüssel → kundenlesbare Texte (vorher stand der rohe
// Slug '30-tage-netto' auf der Angebotsseite)
const PAYMENT_LABELS = {
  '50-50': '50 % des einmaligen Aufwands bei Beauftragung, 50 % bei Fertigstellung. Laufende Leistungen werden monatlich abgerechnet.',
  '50-30-20': '50 % bei Beauftragung, 30 % bei Zwischenabnahme, 20 % bei Fertigstellung',
  'bei-lieferung': 'Zahlbar bei Lieferung',
  '30-tage-netto': 'Zahlbar innerhalb von 30 Tagen netto ohne Abzug',
  '14-tage-netto': 'Zahlbar innerhalb von 14 Tagen netto ohne Abzug',
  'sofort-faellig': 'Sofort fällig ohne Abzug',
};

export const TermsBlock = ({ quote, company, theme }) => {
  const card = {
    background: theme.surface, border: `1px solid ${theme.surfaceBorder}`,
    borderRadius: theme.radius, padding: '20px 24px', color: theme.textPrimary,
  };
  // AGB liegen im Firmen-Feld termsAndConditions (RichText-HTML); 'agb' war ein
  // nie existierendes Feld — dadurch fehlten die AGB in allen Design-Layouts.
  const agbHtml = company?.termsAndConditions || company?.agb || '';
  // Eigener Text hat Vorrang; sonst lesbares Label; roher Slug nie sichtbar
  const paymentText = quote.customPaymentTerms
    || PAYMENT_LABELS[quote.paymentTerms]
    || (quote.paymentTerms && quote.paymentTerms !== 'custom' ? quote.paymentTerms : '');
  const hasAny = paymentText || quote.deliveryTime || quote.warranty || agbHtml;
  if (!hasAny) return null;
  const agbCss = `
    .agb-scroll { max-height: 460px; overflow-y: auto; margin-top: 12px; padding: 18px 20px;
      border: 1px solid ${theme.surfaceBorder}; border-radius: 10px;
      background: ${theme.dark ? 'rgba(255,255,255,0.03)' : '#fafafa'}; }
    .agb-html { font-size: 14.5px; line-height: 1.75; color: ${theme.textSecondary};
      font-family: 'Inter', system-ui, sans-serif; }
    .agb-html h1, .agb-html h2 { font-size: 16.5px; font-weight: 700; color: ${theme.textPrimary}; margin: 20px 0 8px; }
    .agb-html h3, .agb-html h4, .agb-html strong > u, .agb-html b { color: ${theme.textPrimary}; }
    .agb-html h3, .agb-html h4 { font-size: 15px; font-weight: 600; margin: 16px 0 6px; }
    .agb-html p { margin: 0 0 10px; }
    .agb-html ul, .agb-html ol { margin: 0 0 10px; padding-left: 22px; }
    .agb-html li { margin-bottom: 5px; }
    .agb-html strong { color: ${theme.textPrimary}; }
  `;
  return (
    <div style={card}>
      <style>{agbCss}</style>
      <h3 style={{ fontSize: 19, fontWeight: 600, marginBottom: 12 }}>Konditionen</h3>
      <div style={{ fontSize: 15.5, color: theme.textSecondary, lineHeight: 1.75 }}>
        {paymentText && <p><strong style={{ color: theme.textPrimary }}>Zahlung:</strong> {paymentText}</p>}
        {quote.deliveryTime && <p><strong style={{ color: theme.textPrimary }}>Lieferzeit:</strong> {quote.deliveryTime}</p>}
        {quote.warranty && <p><strong style={{ color: theme.textPrimary }}>Gewährleistung:</strong> {quote.warranty}</p>}
        <p><strong style={{ color: theme.textPrimary }}>Gültig bis:</strong> {quoteValidUntil(quote).toLocaleDateString('de-DE')}</p>
      </div>
      {agbHtml && (
        <details style={{ marginTop: 14 }}>
          <summary style={{ cursor: 'pointer', fontSize: 14.5, color: theme.primary, fontWeight: 600 }}>
            Allgemeine Geschäftsbedingungen anzeigen
          </summary>
          <div className="agb-scroll">
            <div className="agb-html" dangerouslySetInnerHTML={{ __html: agbHtml }} />
          </div>
        </details>
      )}
    </div>
  );
};

// ── Feedback ──────────────────────────────────────────────────────────────────
export const FeedbackBlock = ({ viewHash, theme }) => {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!rating) return;
    setBusy(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/view/${viewHash}/feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment }),
      });
      setDone(true); toast.success('Vielen Dank für Ihr Feedback!');
    } catch { toast.error('Fehler beim Senden'); } finally { setBusy(false); }
  };
  const card = { background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, borderRadius: theme.radius, padding: '20px 24px', color: theme.textPrimary };
  if (done) return <div style={card}><p style={{ fontSize: 14, color: theme.textSecondary }}><Check className="w-4 h-4 inline mr-1" style={{ color: '#22c55e' }} />Danke für Ihr Feedback!</p></div>;
  const opt = (val, Icon, label) => (
    <button onClick={() => setRating(val)} style={{
      flex: 1, padding: '10px', borderRadius: theme.radius, cursor: 'pointer',
      background: rating === val ? theme.primary : 'transparent',
      color: rating === val ? '#fff' : theme.textSecondary,
      border: `1px solid ${rating === val ? theme.primary : theme.surfaceBorder}`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, fontSize: 12,
    }}><Icon className="w-5 h-5" />{label}</button>
  );
  return (
    <div style={card}>
      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Wie gefällt Ihnen dieses Angebot?</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {opt('positive', ThumbsUp, 'Gut')}
        {opt('neutral', Minus, 'Neutral')}
        {opt('negative', ThumbsDown, 'Unklar')}
      </div>
      {rating && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Kommentar (optional)"
            style={{ flex: 1, padding: '8px 12px', borderRadius: theme.radius, border: `1px solid ${theme.surfaceBorder}`, background: theme.dark ? '#0f172a' : '#fff', color: theme.textPrimary, fontSize: 13 }} />
          <button onClick={submit} disabled={busy} style={{ padding: '8px 16px', borderRadius: theme.radius, background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13 }}>Senden</button>
        </div>
      )}
    </div>
  );
};

// ── Kundenfrage ───────────────────────────────────────────────────────────────
export const QuestionBlock = ({ viewHash, theme }) => {
  const [form, setForm] = useState({ name: '', email: '', question: '' });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.question) { toast.error('Bitte Name und Frage ausfüllen'); return; }
    setBusy(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/view/${viewHash}/question`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      setDone(true); toast.success('Ihre Frage wurde übermittelt!');
    } catch { toast.error('Fehler beim Senden'); } finally { setBusy(false); }
  };
  const card = { background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, borderRadius: theme.radius, padding: '20px 24px', color: theme.textPrimary };
  const inp = { width: '100%', padding: '9px 12px', borderRadius: theme.radius, border: `1px solid ${theme.surfaceBorder}`, background: theme.dark ? '#0f172a' : '#fff', color: theme.textPrimary, fontSize: 13, marginBottom: 8 };
  if (done) return <div style={card}><p style={{ fontSize: 14, color: theme.textSecondary }}><Check className="w-4 h-4 inline mr-1" style={{ color: '#22c55e' }} />Ihre Frage wurde gesendet.</p></div>;
  return (
    <div style={card}>
      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Haben Sie eine Frage?</h3>
      <form onSubmit={submit}>
        <input style={inp} placeholder="Ihr Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        <input style={inp} placeholder="Ihre E-Mail (optional)" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        <textarea style={{ ...inp, minHeight: 70 }} placeholder="Ihre Frage…" value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} />
        <button type="submit" disabled={busy} style={{ padding: '9px 18px', borderRadius: theme.radius, background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Send className="w-4 h-4" />Frage senden
        </button>
      </form>
    </div>
  );
};

// ── Annahme-CTA / Annahme-Status / Annahme-Formular ──────────────────────────

// Narrative Angebotsabschnitte (quoteSections) — der inhaltliche Kern vieler
// Angebote (Zeitplan, Warum-wir, Zahlungsmodalitäten, …). Wurde bisher nur vom
// Dokument-Layout gerendert; alle Design-Layouts ließen die Abschnitte weg.
// Unterstützt echtes HTML (Tabellen etc.) UND das leichte Markdown des UI-Editors.
export const SectionsBlock = ({ quote, theme, ctx }) => {
  const sections = quote?.quoteSections;
  if (!sections || sections.length === 0) return null;

  // ── Live-Platzhalter: Zahlen in Text-Sektionen rechnen automatisch mit ──
  // {{summe_einmalig}}, {{summe_einmalig_skonto3}}, {{summe_monatlich}},
  // {{summe_jaehrlich}}, {{angebotsnummer}}, {{gueltig_bis}}
  // Reagiert live auf an-/abgewählte optionale Positionen (ctx-Totals).
  const fmtNum = (n) => (Number(n) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  const fillPlaceholders = (html) => {
    if (!html || html.indexOf('{{') === -1) return html;
    const e = ctx?.einmaligTotals?.netto ?? 0;
    const m = ctx?.monatlichTotals?.netto ?? 0;
    const j = ctx?.jaehrlichTotals?.netto ?? 0;
    return html
      .replace(/\{\{summe_einmalig\}\}/g, fmtNum(e))
      .replace(/\{\{summe_einmalig_skonto3\}\}/g, fmtNum(e * 0.97))
      .replace(/\{\{summe_monatlich\}\}/g, fmtNum(m))
      .replace(/\{\{summe_jaehrlich\}\}/g, fmtNum(j))
      .replace(/\{\{angebotsnummer\}\}/g, quote?.quoteNumber || '')
      .replace(/\{\{gueltig_bis\}\}/g, quoteValidUntil(quote).toLocaleDateString('de-DE'));
  };

  const toHtml = (content) => {
    if (!content) return '';
    if (/<\s*(table|p|ul|ol|div|h\d|tr|li)\b/i.test(content)) return fillPlaceholders(content); // echtes HTML
    return fillPlaceholders(content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
      .replace(/\n/g, '<br />'));
  };

  // Theme-Variablen für gestylte HTML-Vorlagen (Karten, Zeitstrahl, Team):
  // Die Vorlagen nutzen var(--qsec-*) und passen sich so jedem Layout an —
  // dunkle Layouts bekommen dunkle Karten + den echten Brand-Akzent statt
  // hart kodiertem Weiß/Amber.
  const css = `
    .qsec-html {
      --qsec-card-bg: ${theme.dark ? 'rgba(255,255,255,0.06)' : '#f8fafc'};
      --qsec-card-border: ${theme.surfaceBorder};
      --qsec-card-text: ${theme.textPrimary};
      --qsec-card-muted: ${theme.textSecondary};
      --qsec-accent: ${theme.accent || theme.primary};
      --qsec-accent-contrast: ${theme.dark ? '#0f172a' : '#ffffff'};
    }
    .qsec-html { font-size: 16.5px; line-height: 1.75; color: ${theme.textSecondary}; }
    .qsec-html p { margin: 0 0 10px; }
    .qsec-html strong { color: ${theme.textPrimary}; }
    .qsec-html a { color: ${theme.primary}; }
    .qsec-html ul, .qsec-html ol { margin: 0 0 10px; padding-left: 20px; }
    .qsec-html li { margin-bottom: 6px; }
    .qsec-html table { width: 100%; border-collapse: collapse; margin: 10px 0 14px; font-size: 14.5px; }
    .qsec-html th, .qsec-html td { border: 1px solid ${theme.surfaceBorder}; padding: 8px 10px; text-align: left; vertical-align: top; }
    .qsec-html th { background: ${theme.pageBg || 'rgba(0,0,0,0.04)'}; color: ${theme.textPrimary}; font-weight: 600; }
    .qsec-html em { color: ${theme.textSecondary}; }
    .qsec-details > summary { list-style: none; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
    .qsec-details > summary::-webkit-details-marker { display: none; }
    .qsec-details > summary::after { content: '+'; font-size: 22px; line-height: 1; color: ${theme.textMuted}; flex-shrink: 0; transition: transform .15s; }
    .qsec-details[open] > summary::after { content: '–'; }
    .qsec-details[open] > summary { margin-bottom: 14px; }
  `;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <style>{css}</style>
      {sections.map((section, i) => {
        const card = {
          background: theme.surface, border: `1px solid ${theme.surfaceBorder}`,
          borderRadius: theme.radius, padding: '22px 26px',
        };
        // Sektionen mit Titel: aufklappbar, standardmäßig ZU — der Kunde sieht
        // erst die Überschriften. Beim Drucken werden alle geöffnet (Print-Flow).
        if (section.title) {
          return (
            <details key={i} className="qsec-details" style={card} open={!!section.defaultOpen}>
              <summary>
                <h3 style={{ fontSize: 21, fontWeight: 600, margin: 0, color: theme.textPrimary, fontFamily: theme.font }}>
                  {section.title}
                </h3>
              </summary>
              <div className="qsec-html" dangerouslySetInnerHTML={{ __html: toHtml(section.content) }} />
            </details>
          );
        }
        return (
          <div key={i} style={card}>
            <div className="qsec-html" dangerouslySetInnerHTML={{ __html: toHtml(section.content) }} />
          </div>
        );
      })}
    </div>
  );
};


// Einheitliche Kosten-Aufschlüsselung für ALLE Design-Layouts (wie im
// Dokument-Layout): je Kostentyp Netto → MwSt. (19 %) → Brutto.
// Netto steht bewusst GROSS (B2B — der Netto-Preis ist die Verkaufszahl),
// Brutto klein darunter. Reagiert live auf an-/abgewählte Optionen.
export const TotalsBlock = ({ ctx, theme }) => {
  const { einmaligTotals, monatlichTotals, jaehrlichTotals } = ctx;
  const fmt = (n) => (Number(n) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  const groups = [
    { label: 'Einmalige Leistungen', t: einmaligTotals, suffix: '' },
    { label: 'Monatliche Leistungen', t: monatlichTotals, suffix: ' / Monat' },
    { label: 'Jährliche Leistungen', t: jaehrlichTotals, suffix: ' / Jahr' },
  ].filter(g => g.t && g.t.netto > 0);
  if (!groups.length) return null;

  // Banner-Design (frueher separater Hero-Block): Primaerfarbe als Flaeche,
  // Schriftfarbe nach Luminanz der Flaeche (helle Brand-Farben -> dunkler Text)
  const bg = theme.primary || '#1e293b';
  const lum = (() => {
    try {
      let h = bg.replace('#', '');
      if (h.length === 3) h = h.split('').map(c => c + c).join('');
      const [r, g, b] = [0, 2, 4].map(i => parseInt(h.substr(i, 2), 16) / 255);
      return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    } catch (e) { return 0; }
  })();
  const darkBg = lum < 0.62;
  const fg = darkBg ? '#ffffff' : '#0f172a';
  const fgSoft = darkBg ? 'rgba(255,255,255,.85)' : 'rgba(15,23,42,.75)';
  const fgMuted = darkBg ? 'rgba(255,255,255,.65)' : 'rgba(15,23,42,.55)';
  const divider = darkBg ? 'rgba(255,255,255,.25)' : 'rgba(15,23,42,.15)';

  return (
    <div style={{ background: bg, borderRadius: theme.radius, padding: '26px 28px', color: fg }}>
      <h3 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: fg, fontFamily: theme.font }}>
        Ihre Investition
      </h3>

      {/* Aufschluesselung je Kostenart — keine Summen-Doppelung im Kopf */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 18 }}>
        {groups.map((g, i) => (
          <div key={i} style={{ borderTop: i > 0 ? `1px solid ${divider}` : 'none', paddingTop: i > 0 ? 18 : 0 }}>
            <p style={{ fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: fgMuted, margin: '0 0 8px' }}>{g.label}</p>
            {/* Rabatt sichtbar ausweisen — vorher wurde er still eingerechnet */}
            {g.t.globalDiscount > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                  <span style={{ fontSize: 13.5, color: fgSoft }}>Zwischensumme</span>
                  <span style={{ fontSize: 13.5, color: fgSoft, textDecoration: 'line-through' }}>{fmt(g.t.finalSubtotal)}{g.suffix}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                  <span style={{ fontSize: 13.5, color: fg, fontWeight: 600 }}>
                    Rabatt{g.t.discountInfo?.type === 'percentage' ? ` (${g.t.discountInfo.value} %)` : ''}
                    {g.t.discountInfo?.reason ? ` — ${g.t.discountInfo.reason}` : ''}
                  </span>
                  <span style={{ fontSize: 13.5, color: fg, fontWeight: 600 }}>−{fmt(g.t.globalDiscount)}{g.suffix}</span>
                </div>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, color: fgSoft }}>Netto{g.t.globalDiscount > 0 ? ' nach Rabatt' : ''}</span>
              <span style={{ fontSize: 22, fontWeight: 700, color: fg }}>{fmt(g.t.netto)}{g.suffix}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 6 }}>
              <span style={{ fontSize: 13.5, color: fgSoft }}>MwSt. (19 %)</span>
              <span style={{ fontSize: 13.5, color: fgSoft }}>{fmt(g.t.mwst)}{g.suffix}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 4 }}>
              <span style={{ fontSize: 13.5, color: fgMuted }}>Brutto (inkl. MwSt.)</span>
              <span style={{ fontSize: 14.5, fontWeight: 500, color: fg }}>{fmt(g.t.brutto)}{g.suffix}</span>
            </div>
          </div>
        ))}
      </div>
      {(ctx.quote?.selectedServices || []).some(s => Number(s.priceMax) > (Number(s.price) || 0)) && (
        <p style={{ fontSize: 12.5, color: fgSoft, margin: '16px 0 0' }}>
          Positionen mit Preisspanne: Ihre Annahme erfolgt zum <strong style={{ color: fg }}>unteren
          Wert — dieser ist verbindlich</strong>. Eine Abweichung innerhalb der Spanne erfolgt nur nach
          vorheriger Abstimmung und Ihrer Freigabe.
        </p>
      )}
      <p style={{ fontSize: 12, color: fgMuted, margin: '16px 0 0' }}>
        Alle Preise netto zzgl. 19 % gesetzlicher Umsatzsteuer.
      </p>
    </div>
  );
};

export const AcceptanceBlock = ({ ctx, theme }) => {
  const { quote, viewHash, quoteAccepted, setQuoteAccepted, showAcceptanceForm, setShowAcceptanceForm,
    optionalServicesSelected, handleSaveAsPdf, refreshQuote } = ctx;
  const companyColors = { primary: theme.primary, einmalig: theme.einmalig, monatlich: theme.monatlich, jaehrlich: theme.jaehrlich };

  if (quoteAccepted) {
    return (
      <div style={{ background: theme.dark ? '#0b2e22' : '#ecfdf5', border: `1px solid ${theme.dark ? '#065f46' : '#a7f3d0'}`, borderRadius: theme.radius, padding: '28px 24px', textAlign: 'center', color: theme.textPrimary }}>
        <Check className="w-14 h-14 mx-auto mb-3" style={{ color: '#10b981' }} />
        <h3 style={{ fontSize: 22, fontWeight: 700, color: theme.dark ? '#6ee7b7' : '#065f46', marginBottom: 6 }}>Angebot angenommen</h3>
        <p style={{ fontSize: 14, color: theme.textSecondary }}>Angenommen am {new Date(quote.acceptedAt).toLocaleString('de-DE')}.</p>
        <button onClick={handleSaveAsPdf} style={{ marginTop: 18, padding: '11px 22px', borderRadius: theme.radius, background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14 }}>
          Rechtsverbindliches PDF herunterladen
        </button>
      </div>
    );
  }

  if (showAcceptanceForm) {
    return (
      <QuoteAcceptanceForm
        viewHash={viewHash}
        optionalServicesSelected={optionalServicesSelected}
        companyColors={companyColors}
        onDownloadPdf={handleSaveAsPdf}
        onAcceptanceComplete={() => { setQuoteAccepted(true); setShowAcceptanceForm(false); refreshQuote(); }}
      />
    );
  }

  return (
    <div style={{ background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, borderRadius: theme.radius, padding: '32px 24px', textAlign: 'center', color: theme.textPrimary }}>
      <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Bereit, loszulegen?</h3>
      <p style={{ fontSize: 14, color: theme.textSecondary, marginBottom: 20 }}>Nehmen Sie das Angebot digital an — mit oder ohne Unterschrift.</p>
      <button onClick={() => setShowAcceptanceForm(true)} style={{ padding: '14px 32px', borderRadius: theme.radius, background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <Check className="w-5 h-5" />Angebot jetzt annehmen
      </button>
    </div>
  );
};

// ── Footer ────────────────────────────────────────────────────────────────────
export const FooterBlock = ({ company, theme }) => (
  <div style={{ textAlign: 'center', fontSize: 12, color: theme.textMuted, paddingTop: 8 }}>
    <p style={{ fontWeight: 500, color: theme.textSecondary }}>{company?.name}</p>
    <p>{company?.address} • {company?.city}</p>
    <p>Tel: {company?.phone} • {company?.email}{company?.website ? ` • ${company.website}` : ''}</p>
  </div>
);
